import type {
  MasterFixture,
  ResponseGetInputsMasterFixturesAll,
} from "@CommonApi/inputs/master-fixtures/all";
import type { PayloadPutInputsMasterFixtures_masterFixtureId_ } from "@CommonApi/inputs/master-fixtures/_masterFixtureId_";

type FixturePayload = PayloadPutInputsMasterFixtures_masterFixtureId_;

/** The fields one cell edit changes; the rest of the PUT comes from the row as last saved. */
export type FixtureEdit = Partial<FixturePayload>;

const editToRow = (edit: FixtureEdit): Partial<MasterFixture> => ({
  ...(edit.maxShelfHeight !== undefined ? { max_shelf_height: edit.maxShelfHeight } : {}),
  ...(edit.maxProductHead !== undefined ? { max_product_head: edit.maxProductHead } : {}),
  ...(edit.dividersSetByHand !== undefined ? { dividers_set_by_hand: edit.dividersSetByHand } : {}),
  ...(edit.loaderDepth !== undefined ? { loader_depth: edit.loaderDepth } : {}),
  ...(edit.maxFrontOverhang !== undefined ? { max_front_overhang: edit.maxFrontOverhang } : {}),
});

/**
 * Saves fixture rows one PUT at a time per row. Each PUT reads the row's
 * height and head when it runs, and a save merges only its own fields into
 * the latest table, so no edit restores another edit's older value.
 */
export const createFixtureRowSaver = (
  getTable: () => ResponseGetInputsMasterFixturesAll | null | undefined,
  setTable: (table: ResponseGetInputsMasterFixturesAll) => void,
) => {
  const queues = new Map<number, Promise<unknown>>();

  return <T>(
    row: MasterFixture,
    edit: FixtureEdit,
    put: (payload: FixturePayload) => Promise<T>,
    isSaved: (result: T) => boolean = () => true,
  ): Promise<T> => {
    const id = row.master_fixture_id;
    const send = async () => {
      const latest = getTable()?.data.find((fixture) => fixture.master_fixture_id === id) ?? row;
      const result = await put({
        maxShelfHeight: latest.max_shelf_height,
        maxProductHead: latest.max_product_head,
        ...edit,
      });
      const table = getTable();
      if (isSaved(result) && table) {
        setTable({
          ...table,
          data: table.data.map((fixture) =>
            fixture.master_fixture_id === id ? { ...fixture, ...editToRow(edit) } : fixture,
          ),
        });
      }
      return result;
    };
    const run = (queues.get(id) ?? Promise.resolve()).then(send);
    queues.set(
      id,
      run.catch(() => undefined),
    );
    return run;
  };
};
